import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputBase, inputClass } from './Field'
import { BankAccount, InterestSettings } from '../types'
import { makeId } from '../lib/id'
import { toISODate } from '../lib/dateRules'
import { normalizeBands } from '../lib/storage'
import { annualInterest, sortRates } from '../lib/interest'
import { formatMoney } from '../lib/format'

interface Props {
  account: BankAccount
  currencySymbol: string
  onSave: (interest: InterestSettings) => void
  onClose: () => void
}

/** A band as typed — strings until saved, so a half-typed number isn't lost. */
interface BandDraft {
  upTo: string
  rate: string
}

interface RateDraft {
  id: string
  /** Empty on the starting rate. */
  from: string
  /** The last one is "the rest" and has no ceiling. */
  bands: BandDraft[]
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1)

function toDraft(account: BankAccount): RateDraft[] {
  const rates = account.interest?.rates ?? []
  if (rates.length === 0) return [{ id: makeId(), from: '', bands: [{ upTo: '', rate: '' }] }]
  return sortRates(rates).map((r) => ({
    id: r.id,
    from: r.from ?? '',
    bands: r.bands.map((b) => ({ upTo: b.upTo?.toString() ?? '', rate: b.rate.toString() })),
  }))
}

function bandsOf(draft: RateDraft) {
  return normalizeBands(
    draft.bands.map((b, i) => ({
      // Only the last band is open-ended, whatever was typed into it.
      upTo: i < draft.bands.length - 1 ? parseFloat(b.upTo) : undefined,
      rate: parseFloat(b.rate) || 0,
    })),
  )
}

/**
 * Interest for one account: the bands, when it's paid, and how the rate has
 * changed. A change is added as a new dated rate rather than an edit, so the
 * interest earned under the old one stays as it was.
 */
export default function InterestForm({ account, currencySymbol, onSave, onClose }: Props) {
  const [enabled, setEnabled] = useState(account.interest?.enabled ?? true)
  const [paymentDay, setPaymentDay] = useState(String(account.interest?.paymentDay ?? 1))
  const [rates, setRates] = useState<RateDraft[]>(() => toDraft(account))
  const [error, setError] = useState<string | null>(null)

  function updateRate(id: string, change: (r: RateDraft) => RateDraft) {
    setRates((prev) => prev.map((r) => (r.id === id ? change(r) : r)))
  }

  function updateBand(rateId: string, index: number, field: keyof BandDraft, value: string) {
    updateRate(rateId, (r) => ({ ...r, bands: r.bands.map((b, i) => (i === index ? { ...b, [field]: value } : b)) }))
  }

  /** A new band goes in just before "the rest". */
  function addBand(rateId: string) {
    updateRate(rateId, (r) => ({ ...r, bands: [...r.bands.slice(0, -1), { upTo: '', rate: '' }, r.bands[r.bands.length - 1]] }))
  }

  function removeBand(rateId: string, index: number) {
    updateRate(rateId, (r) => ({ ...r, bands: r.bands.filter((_, i) => i !== index) }))
  }

  /** Starts from the latest bands, since a rate change usually moves one number. */
  function addRateChange() {
    const latest = rates[rates.length - 1]
    setRates((prev) => [
      ...prev,
      { id: makeId(), from: toISODate(new Date()), bands: latest.bands.map((b) => ({ ...b })) },
    ])
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const [first, ...changes] = rates
    const dates = changes.map((r) => r.from)
    if (dates.some((d) => !d)) {
      setError('Every rate change needs the date it starts.')
      return
    }
    if (new Set(dates).size !== dates.length) {
      setError('Two rate changes start on the same day.')
      return
    }
    onSave({
      enabled,
      paymentDay: paymentDay === 'last' ? 'last' : parseInt(paymentDay, 10),
      rates: [
        { id: first.id, bands: bandsOf(first) },
        ...changes.map((r) => ({ id: r.id, from: r.from, bands: bandsOf(r) })),
      ].filter((r) => r.bands.length > 0),
    })
  }

  // A worked example keeps the band maths honest: it's easy to read "first
  // £500 at 4.5%" as the whole balance earning 4.5% until it passes £500.
  const example = Math.max(account.settings.startingBalance, 1000)
  const latestBands = bandsOf(rates[rates.length - 1])

  return (
    <Modal title={`Interest on ${account.name}`} onClose={onClose} width="max-w-lg">
      <form onSubmit={handleSubmit}>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="text-sm text-ink">This account earns interest</span>
        </label>

        <fieldset disabled={!enabled} className={enabled ? '' : 'opacity-50'}>
          <Field
            label="Paid on"
            hint="Worked out daily on each day's closing balance, and added to the account on this day each month."
          >
            <select className={inputClass} value={paymentDay} onChange={(e) => setPaymentDay(e.target.value)}>
              {DAYS.map((d) => (
                <option key={d} value={d}>
                  Day {d}
                  {d > 28 ? ' (or the last day, in shorter months)' : ''}
                </option>
              ))}
              <option value="last">Last day of the month</option>
            </select>
          </Field>

          {rates.map((rate, ri) => (
            <div key={rate.id} className="border-t border-line pt-3 mb-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                {ri === 0 ? (
                  <h4 className="text-xs font-sans uppercase tracking-wide text-ink/70">
                    {rates.length > 1 ? 'Starting rate' : 'Rate'}
                  </h4>
                ) : (
                  <label className="flex items-center gap-2 text-xs text-ink/75">
                    <span className="uppercase tracking-wide text-ink/70">Changes on</span>
                    <input
                      type="date"
                      className={inputBase}
                      value={rate.from}
                      onChange={(e) => updateRate(rate.id, (r) => ({ ...r, from: e.target.value }))}
                      required
                    />
                  </label>
                )}
                {ri > 0 && (
                  <button
                    type="button"
                    onClick={() => setRates((prev) => prev.filter((r) => r.id !== rate.id))}
                    className="text-xs text-rag-bad hover:underline"
                  >
                    Remove
                  </button>
                )}
              </div>

              <ul className="space-y-1.5">
                {rate.bands.map((band, bi) => {
                  const isRest = bi === rate.bands.length - 1
                  return (
                    <li key={bi} className="flex items-center gap-2 text-sm text-ink/80">
                      {isRest ? (
                        <span className="w-36 shrink-0">{rate.bands.length > 1 ? 'Everything above' : 'The whole balance'}</span>
                      ) : (
                        <span className="w-36 shrink-0 flex items-center gap-1">
                          Up to {currencySymbol}
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label="Top of this band"
                            className={`${inputBase} w-20`}
                            value={band.upTo}
                            onChange={(e) => updateBand(rate.id, bi, 'upTo', e.target.value)}
                            required
                          />
                        </span>
                      )}
                      <span>at</span>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        aria-label="Annual rate"
                        className={`${inputBase} w-20 text-right`}
                        value={band.rate}
                        onChange={(e) => updateBand(rate.id, bi, 'rate', e.target.value)}
                        required
                      />
                      <span>% a year</span>
                      {!isRest && (
                        <button
                          type="button"
                          onClick={() => removeBand(rate.id, bi)}
                          aria-label="Remove this band"
                          className="text-ink/70 hover:text-rag-bad ml-auto"
                        >
                          ×
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
              <button type="button" onClick={() => addBand(rate.id)} className="text-xs text-accent hover:underline mt-2">
                + Add a band
              </button>
            </div>
          ))}

          <button type="button" onClick={addRateChange} className="text-sm text-accent hover:underline mb-1">
            + Add a rate change
          </button>
          <p className="text-[11px] text-ink/70 mb-4">
            A change applies from its date on. Interest already earned at the old rate stays as it was.
          </p>

          {latestBands.length > 0 && (
            <p className="text-xs text-ink/75 bg-paper rounded-sm px-3 py-2 mb-4">
              At the{rates.length > 1 ? ' latest' : ''} rate, {formatMoney(example, currencySymbol)} held for a year earns
              about <span className="font-mono">{formatMoney(annualInterest(example, latestBands), currencySymbol)}</span>.
            </p>
          )}
        </fieldset>

        {error && <p className="text-sm text-rag-bad mb-3">{error}</p>}

        <div className="flex justify-end gap-2 pt-3 border-t border-line">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
            Cancel
          </button>
          <button type="submit" className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}
